import { useSyncExternalStore } from "react";
import { demoApi } from "@/api/client";
import type { EntityTax } from "@/api/types";

type Snapshot = {
  state: "idle" | "loading" | "ready" | "error";
  rows: ReadonlyMap<string, EntityTax>;
  error: string | null;
};
const empty: Snapshot = { state: "idle", rows: new Map(), error: null };
let snapshot = empty;
let controller: AbortController | null = null;
const listeners = new Set<() => void>();
const getSnapshot = () => snapshot;
function publish(next: Snapshot): void {
  snapshot = next;
  listeners.forEach((listener) => listener());
}

// Server tax-state DTOs only (the mask, never the number): no input or request body is cached here.
export function updateEntityTax(tax: EntityTax): void {
  const rows = new Map(snapshot.rows);
  rows.set(tax.entityId, tax);
  publish({ ...snapshot, rows });
}

function load(): void {
  if (snapshot.state !== "idle" || listeners.size === 0) return;
  const request = new AbortController();
  controller = request;
  publish({ ...snapshot, state: "loading", error: null });
  void demoApi.getEntityTaxList(request.signal).then((response) => {
    if (request.signal.aborted) return;
    const rows = new Map(response.entities.map((tax) => [tax.entityId, tax] as const));
    // A save completed during the list request: its newer answer wins.
    snapshot.rows.forEach((tax, id) => rows.set(id, tax));
    publish({ state: "ready", rows, error: null });
  }).catch(() => {
    if (!request.signal.aborted) publish({ ...snapshot, state: "error", error: "Could not load tax identity." });
  });
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  queueMicrotask(load);
  return () => {
    listeners.delete(listener);
    queueMicrotask(() => {
      if (listeners.size !== 0) return;
      controller?.abort();
      controller = null;
      snapshot = empty;
    });
  };
}

export function retryEntityTaxes(): void {
  if (snapshot.state !== "error") return;
  publish({ ...snapshot, state: "idle", error: null });
  load();
}

export function refreshEntityTaxes(): void {
  if (snapshot.state !== "ready") return;
  publish({ ...snapshot, state: "idle" });
  load();
}

export function useEntityTaxStore(): Snapshot {
  return useSyncExternalStore(subscribe, getSnapshot, () => empty);
}

/** Words of an entity's tax status (a payee's words, plus the legacy case). Display only: the server decides readiness. */
export function entityTaxLabel(tax: Pick<EntityTax, "status" | "missing" | "legacyPending">): string {
  if (tax.status === "ready") return "Ready for 1099";
  if (tax.legacyPending) return "Missing tax ID - re-enter the number from the old system";
  const taxId = tax.missing.includes("taxId");
  const address = tax.missing.includes("address");
  if (taxId && address) return "Missing tax ID and address";
  return taxId ? "Missing tax ID" : "Missing address";
}

export function entityTaxState(tax: Pick<EntityTax, "status" | "legacyPending">): "ready" | "legacy" | "missing" {
  if (tax.status === "ready") return "ready";
  return tax.legacyPending ? "legacy" : "missing";
}
